import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkles, Zap, Building2, User } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface UserSummary {
  userId: string;
  email: string;
  name: string;
  role: string;
  aiBalance: number;
  leadBalance: number;
}

interface CreditSnapshot {
  workspace: {
    ai: { balance: number; mode: string };
    lead: { grantBalance: number; purchasedBalance: number; total: number; mode: string };
  };
  users: UserSummary[];
}

interface CreditsDialogProps {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  workspaceName: string;
  defaultTab?: "ai" | "lead";
}

export function CreditsDialog({ open, onClose, workspaceId, workspaceName, defaultTab = "ai" }: CreditsDialogProps) {
  const { toast } = useToast();

  const [aiAmount, setAiAmount] = useState("");
  const [aiTarget, setAiTarget] = useState("workspace");
  const [aiNotes, setAiNotes] = useState("");

  const [leadAmount, setLeadAmount] = useState("");
  const [leadTarget, setLeadTarget] = useState("workspace");
  const [leadNotes, setLeadNotes] = useState("");

  const { data, isLoading, refetch } = useQuery<CreditSnapshot>({
    queryKey: ["/api/platform/workspaces", workspaceId, "credits"],
    queryFn: () => apiRequest("GET", `/api/platform/workspaces/${workspaceId}/credits`).then((r) => r.json()),
    enabled: open && !!workspaceId,
  });

  const giftAiMutation = useMutation({
    mutationFn: ({ target, credits, notes }: { target: string; credits: number; notes: string }) => {
      const url =
        target === "workspace"
          ? `/api/platform/workspaces/${workspaceId}/gift-ai-credits`
          : `/api/platform/workspaces/${workspaceId}/users/${target}/gift-ai-credits`;
      // 1 credit = $0.10 = 10¢. AI balances are stored as dollar-value cents,
      // so convert the entered credits to cents before sending (true credits UI).
      return apiRequest("POST", url, { credits: credits * 10, notes }).then((r) => r.json());
    },
    onSuccess: (_d, { credits, target }) => {
      const who =
        target === "workspace"
          ? "workspace pool"
          : (data?.users.find((u) => u.userId === target)?.name ?? "user");
      toast({ title: "AI credits added", description: `${credits.toLocaleString()} AI credit${credits !== 1 ? "s" : ""} added to ${who}.` });
      setAiAmount("");
      setAiNotes("");
      refetch();
    },
    onError: (e: any) =>
      toast({ title: "Failed to add AI credits", description: e?.message, variant: "destructive" }),
  });

  const giftLeadMutation = useMutation({
    mutationFn: ({ target, credits, notes }: { target: string; credits: number; notes: string }) => {
      const url =
        target === "workspace"
          ? `/api/platform/workspaces/${workspaceId}/gift-lead-credits`
          : `/api/platform/workspaces/${workspaceId}/users/${target}/gift-lead-credits`;
      return apiRequest("POST", url, { credits, notes }).then((r) => r.json());
    },
    onSuccess: (_d, { credits, target }) => {
      const who =
        target === "workspace"
          ? "workspace pool"
          : (data?.users.find((u) => u.userId === target)?.name ?? "user");
      toast({ title: "Lead credits added", description: `${credits.toLocaleString()} lead credit${credits !== 1 ? "s" : ""} added to ${who}.` });
      setLeadAmount("");
      setLeadNotes("");
      refetch();
    },
    onError: (e: any) =>
      toast({ title: "Failed to add lead credits", description: e?.message, variant: "destructive" }),
  });

  const targetOptions = [
    { value: "workspace", label: "Workspace (shared pool)" },
    ...(data?.users ?? []).map((u) => ({
      value: u.userId,
      label: `${u.name !== u.email ? u.name + " — " : ""}${u.email}`,
    })),
  ];

  const handleClose = () => {
    setAiAmount(""); setAiNotes(""); setAiTarget("workspace");
    setLeadAmount(""); setLeadNotes(""); setLeadTarget("workspace");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Credits — {workspaceName}</DialogTitle>
          <DialogDescription>
            View current balances and add credits to the workspace pool or a specific user.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue={defaultTab} className="mt-2">
          <TabsList className="w-full grid grid-cols-2">
            <TabsTrigger value="ai" className="flex items-center gap-1.5" data-testid="tab-credits-ai">
              <Sparkles className="h-3.5 w-3.5" /> AI Credits
            </TabsTrigger>
            <TabsTrigger value="lead" className="flex items-center gap-1.5" data-testid="tab-credits-lead">
              <Zap className="h-3.5 w-3.5" /> Lead Credits
            </TabsTrigger>
          </TabsList>

          {/* ── AI Credits tab ─────────────────────────────────────────── */}
          <TabsContent value="ai" className="space-y-4 mt-4">
            <div className="rounded-lg border bg-muted/30 p-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                <Building2 className="h-3.5 w-3.5" /> Workspace Pool
              </div>
              {isLoading ? (
                <Skeleton className="h-8 w-28" />
              ) : (
                <div className="flex items-baseline gap-1.5">
                  <span className="text-3xl font-bold tabular-nums">
                    {((data?.workspace.ai.balance ?? 0) / 10).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </span>
                  <span className="text-sm text-muted-foreground">credits</span>
                </div>
              )}
              {data && (
                <p className="text-xs text-muted-foreground mt-1 capitalize">
                  Mode: {data.workspace.ai.mode.replace(/_/g, " ")}
                </p>
              )}
            </div>

            {!isLoading && (data?.users ?? []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <User className="h-3 w-3" /> Per-User Allocations
                </p>
                <div className="rounded-lg border divide-y text-sm">
                  {(data?.users ?? []).map((u) => (
                    <div key={u.userId} className="flex items-center justify-between px-3 py-2.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="truncate">
                          <span className="font-medium">{u.name !== u.email ? u.name : ""}</span>
                          {u.name !== u.email && <span className="text-muted-foreground text-xs ml-1">({u.email})</span>}
                          {u.name === u.email && <span className="text-muted-foreground">{u.email}</span>}
                        </div>
                        <Badge variant="secondary" className="text-xs shrink-0">{u.role}</Badge>
                      </div>
                      <span className="font-mono font-semibold tabular-nums shrink-0 ml-3">
                        {(u.aiBalance / 10).toLocaleString(undefined, { maximumFractionDigits: 2 })} cr
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-lg border p-4 space-y-3">
              <p className="text-sm font-semibold">Add AI Credits</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Credits to add</Label>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    placeholder="100"
                    value={aiAmount}
                    onChange={(e) => setAiAmount(e.target.value)}
                    data-testid="input-credits-ai-amount"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Add to</Label>
                  <Select value={aiTarget} onValueChange={setAiTarget}>
                    <SelectTrigger data-testid="select-credits-ai-target">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {targetOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Notes (optional)</Label>
                <Input
                  placeholder="Reason for adding credits…"
                  value={aiNotes}
                  onChange={(e) => setAiNotes(e.target.value)}
                  data-testid="input-credits-ai-notes"
                />
              </div>
              <Button
                className="w-full"
                disabled={!aiAmount || parseInt(aiAmount) <= 0 || giftAiMutation.isPending}
                onClick={() =>
                  giftAiMutation.mutate({ target: aiTarget, credits: parseInt(aiAmount), notes: aiNotes })
                }
                data-testid="button-credits-ai-add"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                {giftAiMutation.isPending ? "Adding…" : "Add AI Credits"}
              </Button>
            </div>
          </TabsContent>

          {/* ── Lead Credits tab ────────────────────────────────────────── */}
          <TabsContent value="lead" className="space-y-4 mt-4">
            <div className="rounded-lg border bg-muted/30 p-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                <Building2 className="h-3.5 w-3.5" /> Workspace Pool
              </div>
              {isLoading ? (
                <Skeleton className="h-8 w-28" />
              ) : (
                <div className="flex items-baseline gap-1.5">
                  <span className="text-3xl font-bold tabular-nums">
                    {(data?.workspace.lead.total ?? 0).toLocaleString()}
                  </span>
                  <span className="text-sm text-muted-foreground">total credits</span>
                </div>
              )}
              {data && (
                <div className="flex gap-5 text-xs text-muted-foreground mt-2">
                  <span>
                    Monthly grant:{" "}
                    <strong className="text-foreground">
                      {data.workspace.lead.grantBalance.toLocaleString()}
                    </strong>
                  </span>
                  <span>
                    Purchased / rollover:{" "}
                    <strong className="text-foreground">
                      {data.workspace.lead.purchasedBalance.toLocaleString()}
                    </strong>
                  </span>
                </div>
              )}
              {data && (
                <p className="text-xs text-muted-foreground mt-1 capitalize">
                  Mode: {data.workspace.lead.mode.replace(/_/g, " ")}
                </p>
              )}
            </div>

            {!isLoading && (data?.users ?? []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <User className="h-3 w-3" /> Per-User Allocations
                </p>
                <div className="rounded-lg border divide-y text-sm">
                  {(data?.users ?? []).map((u) => (
                    <div key={u.userId} className="flex items-center justify-between px-3 py-2.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="truncate">
                          <span className="font-medium">{u.name !== u.email ? u.name : ""}</span>
                          {u.name !== u.email && <span className="text-muted-foreground text-xs ml-1">({u.email})</span>}
                          {u.name === u.email && <span className="text-muted-foreground">{u.email}</span>}
                        </div>
                        <Badge variant="secondary" className="text-xs shrink-0">{u.role}</Badge>
                      </div>
                      <span className="font-mono font-semibold tabular-nums shrink-0 ml-3">
                        {u.leadBalance.toLocaleString()} cr
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-lg border p-4 space-y-3">
              <p className="text-sm font-semibold">Add Lead Credits</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Credits to add</Label>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    placeholder="50"
                    value={leadAmount}
                    onChange={(e) => setLeadAmount(e.target.value)}
                    data-testid="input-credits-lead-amount"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Add to</Label>
                  <Select value={leadTarget} onValueChange={setLeadTarget}>
                    <SelectTrigger data-testid="select-credits-lead-target">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {targetOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Notes (optional)</Label>
                <Input
                  placeholder="Reason for adding credits…"
                  value={leadNotes}
                  onChange={(e) => setLeadNotes(e.target.value)}
                  data-testid="input-credits-lead-notes"
                />
              </div>
              <Button
                className="w-full"
                disabled={!leadAmount || parseInt(leadAmount) <= 0 || giftLeadMutation.isPending}
                onClick={() =>
                  giftLeadMutation.mutate({ target: leadTarget, credits: parseInt(leadAmount), notes: leadNotes })
                }
                data-testid="button-credits-lead-add"
              >
                <Zap className="h-4 w-4 mr-2" />
                {giftLeadMutation.isPending ? "Adding…" : "Add Lead Credits"}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
